import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { BiometricCatalogService, type BloodTestPackage, type BloodTestPackageCreate, type BloodTestPackageUpdate } from "@/lib/api_client";
import { getAuthHeaders } from "@/lib/api";
import { fetchAll } from "./fetchAll";

export const useBloodTestPackages = () =>
  useQuery({
    queryKey: ["blood-test-packages"],
    queryFn: () => fetchAll<BloodTestPackage>(BiometricCatalogService.getApiBloodTestPackages),
    staleTime: 5 * 60_000,
  });

export const useBloodTestPackage = (id: string) =>
  useQuery({
    queryKey: ["blood-test-package", id],
    queryFn: async () => {
      const res = await BiometricCatalogService.getApiBloodTestPackagesById({ path: { id }, headers: getAuthHeaders() });
      if (!res.data) throw new Error("Not found");
      return res.data as unknown as BloodTestPackage;
    },
    enabled: !!id,
  });

export const useCreateBloodTestPackage = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: BloodTestPackageCreate) => {
      const res = await BiometricCatalogService.postApiBloodTestPackages({ body: data, headers: getAuthHeaders() });
      if (!res.data) throw new Error("Failed");
      return res.data as unknown as BloodTestPackage;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["blood-test-packages"] }),
  });
};

export const useUpdateBloodTestPackage = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: BloodTestPackageUpdate }) => {
      const res = await BiometricCatalogService.putApiBloodTestPackagesById({ path: { id }, body: data, headers: getAuthHeaders() });
      if (!res.data) throw new Error("Failed");
      return res.data as unknown as BloodTestPackage;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["blood-test-packages"] }),
  });
};

export const useDeleteBloodTestPackage = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await BiometricCatalogService.deleteApiBloodTestPackagesById({ path: { id }, headers: getAuthHeaders() });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["blood-test-packages"] }),
  });
};
